<template>
  <ValidationObserver ref="observer">
    <template #default="{ handleSubmit, invalid }">
      <form @submit.prevent="handleSubmit(onSubmit)">
        <BaseLayoutSpaced loose>
          <BaseLayoutSpaced relaxed>
            <ValidationProvider
              ref="name"
              vid="name"
              rules="required|max:255"
              name="name"
              tag="div"
              class="w-1/2"
            >
              <template #default="{ errors, failed }">
                <BaseFormGroup>
                  <BaseFormLabel for="name"> Name * </BaseFormLabel>
                  <BaseFormInput id="name" v-model="data.name" :disabled="disabled" />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </template>
            </ValidationProvider>

            <ValidationProvider
              ref="agencyUuid"
              vid="agencyUuid"
              rules="required|max:255"
              name="agencyUuid"
              tag="div"
              class="w-48"
            >
              <template #default="{ errors, failed }">
                <BaseFormGroup>
                  <BaseFormLabel for="agencyUuid"> Agency * </BaseFormLabel>
                  <BaseFormSelect
                    id="agencyUuid"
                    v-model="data.agencyUuid"
                    :options="agencyOptions"
                    :invalid="failed"
                    :disabled="disabled"
                    class="w-full"
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </template>
            </ValidationProvider>

            <ValidationProvider
              ref="membershipProductId"
              vid="membershipProductId"
              rules="required"
              name="membershipProductId"
              tag="div"
              class="max-w-lg"
            >
              <template #default="{ errors, failed }">
                <BaseFormGroup>
                  <BaseFormLabel for="membershipProductId"> Membership product * </BaseFormLabel>
                  <BaseFormSelect
                    id="membershipProductId"
                    v-model="data.membershipProductId"
                    :options="membershipProductOptions"
                    :invalid="failed"
                    :disabled="disabled"
                    class="w-full"
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </template>
            </ValidationProvider>

            <ValidationProvider
              rules="required|max:255"
              name="dateStart"
              vid="dateStart"
              tag="div"
              class="w-1/3"
            >
              <template #default="{ errors, failed }">
                <BaseFormGroup>
                  <BaseFormLabel for="dateStart"> Date Start * </BaseFormLabel>
                  <BaseFormInput
                    id="dateStart"
                    v-model="data.dateStart"
                    type="date"
                    :disabled="disabled"
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </template>
            </ValidationProvider>

            <div>
              <h5 class="mb-4">Billing</h5>
              <div class="flex -mx-4">
                <div class="flex-none px-4">
                  <ValidationProvider
                    rules="required"
                    vid="billingPeriod"
                    name="billingPeriod"
                    tag="div"
                  >
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="billingPeriod"> Period * </BaseFormLabel>
                        <BaseFormSelect
                          id="billingPeriod"
                          v-model="data.billingPeriod"
                          :options="billingPeriodOptions"
                          :invalid="failed"
                          :disabled="disabled"
                          class="block"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
                <div class="flex-none px-4">
                  <ValidationProvider
                    rules="required|min:1"
                    vid="billingInterval"
                    name="billingInterval"
                    tag="div"
                    class="w-32"
                  >
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="billingInterval"> Interval * </BaseFormLabel>
                        <BaseFormInput
                          id="billingInterval"
                          v-model.number="data.billingInterval"
                          type="number"
                          min="1"
                          :disabled="disabled"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
                <div class="flex-none px-4">
                  <ValidationProvider
                    rules="required|min:1"
                    name="billingLength"
                    vid="billingLength"
                    tag="div"
                    class="w-32"
                  >
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="billingLength"> Length * </BaseFormLabel>
                        <BaseFormInput
                          id="billingLength"
                          v-model.number="data.billingLength"
                          type="number"
                          min="1"
                          :disabled="disabled"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
                <div class="flex-none px-4">
                  <ValidationProvider
                    rules="min:0"
                    name="billingAmount"
                    vid="billingAmount"
                    tag="div"
                    class="w-32"
                  >
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="billingAmount"> Amount </BaseFormLabel>
                        <BaseFormInput
                          id="billingAmount"
                          v-model.number="data.billingAmount"
                          type="number"
                          min="0"
                          step="0.01"
                          :disabled="disabled"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
                <div class="flex-none px-4">
                  <ValidationProvider
                    rules="min:0"
                    name="minCharges"
                    vid="minCharges"
                    tag="div"
                    class="w-32"
                  >
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="minCharges"> Min Budget </BaseFormLabel>
                        <BaseFormInput
                          id="minCharges"
                          v-model.number="data.minCharges"
                          type="number"
                          min="0"
                          step="0.01"
                          :disabled="disabled"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
              </div>
              <p class="mt-4 font-sm italic text-teal-700">
                {{ billing }}
              </p>
            </div>

            <div>
              <h5 class="mb-4">Strategy and Products</h5>
              <div class="flex -mx-4">
                <div class="flex-1 px-4">
                  <ValidationProvider name="strategicPriority" vid="strategicPriority" tag="div">
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="strategicPriority"> Strategy Priority </BaseFormLabel>
                        <BaseFormTextarea
                          id="strategicPriority"
                          v-model="data.strategicPriority"
                          rows="10"
                          :disabled="disabled"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
                <div class="flex-1 px-4">
                  <ValidationProvider name="products" vid="products" tag="div">
                    <template #default="{ errors, failed }">
                      <BaseFormGroup>
                        <BaseFormLabel for="products"> Products </BaseFormLabel>
                        <BaseFormTextarea
                          id="products"
                          v-model="data.products"
                          rows="10"
                          :disabled="disabled"
                        />
                        <BaseFormInlineMessage v-if="failed" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </BaseLayoutSpaced>
          <div>
            <BaseButton type="submit" primary :disabled="disabled || invalid"> Submit </BaseButton>

            <BaseButton type="button" ghost :disabled="disabled" class="ml-2" @click="onCancel">
              Cancel
            </BaseButton>
          </div>
        </BaseLayoutSpaced>
      </form>
    </template>
  </ValidationObserver>
</template>

<script>
import pluralize from 'pluralize';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

export default {
  mixins: [veeValidateErrorsMixin],

  props: {
    defaultData: {
      type: Object,
      required: true,
    },
    agencyOptions: {
      type: Array,
      default: () => [],
    },
    billingPeriodOptions: {
      type: Array,
      default: () => [],
    },
    membershipProductOptions: {
      type: Array,
      default: () => [],
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
    disabled: Boolean,
  },

  data() {
    return {
      data: {
        name: '',
        agencyUuid: null,
        startDate: null,
        billingPeriod: 'month',
        billingInterval: 1,
        billingLength: 12,
        billingAmmount: '',
        minCharges: '',
        strategicPriority: '',
        products: '',
        ...this.defaultData,
      },
    };
  },

  computed: {
    exists() {
      return this.defaultData.uuid !== undefined && this.defaultData.uuid !== null;
    },
    billing() {
      const { billingPeriod, billingInterval, billingLength, billingAmount, minCharges } =
        this.data;

      const totalAmount = (billingAmount || 0) + (minCharges || 0);

      const parts = [
        ...(totalAmount ? [`${totalAmount} €`] : []),
        'every',
        billingInterval,
        pluralize(billingPeriod, billingInterval),
        'for a total of',
        billingLength * billingInterval,
        pluralize(billingPeriod, billingLength * billingInterval),
      ];
      return parts.join(' ');
    },
  },

  watch: {
    serverErrors: {
      deep: true,
      handler: 'applyServerErrors',
    },
  },

  methods: {
    onSubmit() {
      this.$emit('account-subscription-form:submit', this.data);
    },

    onCancel() {
      if (this.disabled) return;

      this.$emit('account-subscription-form:cancel');
    },

    applyServerErrors(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
    },
  },
};
</script>
